import { useMemo, useCallback } from "react";
import _ from "lodash";
import { useTranslation } from "react-i18next";

const CUSTOM_GROUP_KEY = "custom-columns";

export const useManageColumnsDropdownData = ({
  selectedGradeId,
  academicRecordsData,
  curriculumProgramsData,
  transcriptGradeScalesData,
  selectedKeys,
  onSelectionChange,
  idPrefix = "",
}) => {
  const { t } = useTranslation(["transcript", "common"]);

  const curriculumProgramsById = useMemo(
    () =>
      _.reduce(
        curriculumProgramsData,
        (acc, item) => {
          const id = _.toString(_.get(item, "id", ""));
          if (!id) return acc;
          acc[id] = item;
          return acc;
        },
        {}
      ),
    [curriculumProgramsData]
  );

  const transcriptGradesById = useMemo(
    () =>
      _.reduce(
        _.get(academicRecordsData, "node.transcriptSummary.grades", []),
        (acc, grade) => {
          const id = _.toString(_.get(grade, "id", ""));
          if (!id) return acc;
          acc[id] = grade;
          return acc;
        },
        {}
      ),
    [academicRecordsData]
  );

  const getSchoolGradeScales = useCallback(
    schoolSourceId => {
      if (!schoolSourceId) return [];
      const program = _.get(curriculumProgramsById, _.toString(schoolSourceId));
      const edges = _.get(program, "gradeScale.edges", []);
      return _.map(edges, edge => ({
        id: _.get(edge, "node.id"),
        displayName: _.get(edge, "node.displayName"),
      }));
    },
    [curriculumProgramsById]
  );

  const getCustomColumns = useCallback(
    () =>
      _.map(
        _.filter(transcriptGradeScalesData, item => _.get(item, "isCustom")),
        item => ({
          id: _.get(item, "id"),
          displayName:
            _.get(item, "name") || _.get(item, "source.displayName", ""),
        })
      ),
    [transcriptGradeScalesData]
  );

  const transcriptToSourceIdMap = useMemo(
    () =>
      _.reduce(
        transcriptGradeScalesData,
        (acc, item) => {
          const transcriptId = _.get(item, "id");
          const sourceId = _.get(item, "source.id");
          const isCustom = _.get(item, "isCustom", false);

          if (isCustom || !transcriptId || !sourceId) {
            return acc;
          }

          acc[transcriptId] = sourceId;
          return acc;
        },
        {}
      ),
    [transcriptGradeScalesData]
  );

  const sourceToTranscriptIdMap = useMemo(
    () =>
      _.reduce(
        transcriptGradeScalesData,
        (acc, item) => {
          const transcriptId = _.get(item, "id");
          const sourceId = _.get(item, "source.id");
          const isCustom = _.get(item, "isCustom", false);

          if (isCustom || !transcriptId || !sourceId) {
            return acc;
          }

          acc[_.toString(sourceId)] = _.toString(transcriptId);
          return acc;
        },
        {}
      ),
    [transcriptGradeScalesData]
  );

  const buildOptionItems = useCallback(
    ({ columns, parentKey, sourceId }) =>
      _.compact(
        _.map(columns, column => {
          const rawId = _.toString(_.get(column, "id", ""));
          if (!rawId) return null;

          const mappedTranscriptId = _.get(sourceToTranscriptIdMap, rawId);
          const persistedRawId = mappedTranscriptId || rawId;
          const key = idPrefix ? `${idPrefix}${rawId}` : rawId;
          const persistedKey = idPrefix
            ? `${idPrefix}${persistedRawId}`
            : persistedRawId;

          return {
            key,
            persistedKey,
            label: _.get(column, "displayName") || _.get(column, "name", ""),
            parent: parentKey,
            sourceId,
          };
        })
      ),
    [idPrefix, sourceToTranscriptIdMap]
  );

  const dropdownOptions = useMemo(() => {
    if (!academicRecordsData) return [];

    const groupedOptions = [];
    const customLabel = t("transcript:custom_column_other");

    if (selectedGradeId) {
      const gradeData = _.get(
        transcriptGradesById,
        _.toString(selectedGradeId),
        null
      );

      if (gradeData) {
        const schoolName = _.get(gradeData, "school.name", "");
        const schoolSourceId = _.get(gradeData, "school.sourceId");
        const schoolKey = schoolSourceId || _.get(gradeData, "school.id");

        const schoolOptions = buildOptionItems({
          columns: getSchoolGradeScales(schoolSourceId),
          parentKey: schoolKey,
          sourceId: schoolSourceId,
        });

        if (!_.isEmpty(schoolOptions)) {
          groupedOptions.push({
            key: schoolKey,
            label: schoolName,
            isItemGroup: true,
            options: schoolOptions,
          });
        }
      }

      const customOptions = buildOptionItems({
        columns: getCustomColumns(),
        parentKey: CUSTOM_GROUP_KEY,
        sourceId: null,
      });

      if (!_.isEmpty(customOptions)) {
        groupedOptions.push({
          key: CUSTOM_GROUP_KEY,
          label: customLabel,
          isItemGroup: true,
          options: customOptions,
        });
      }

      return groupedOptions;
    }

    const grades = _.get(
      academicRecordsData,
      "node.transcriptSummary.grades",
      []
    );
    const schoolMap = _.reduce(
      grades,
      (acc, grade) => {
        const schoolSourceId = _.get(grade, "school.sourceId");
        if (!schoolSourceId) return acc;

        const schoolId = _.get(grade, "school.id");
        const key = schoolSourceId || schoolId;
        if (!key) return acc;

        if (!acc[key]) {
          acc[key] = {
            key,
            label: _.get(grade, "school.name", ""),
            sourceId: schoolSourceId,
          };
        }

        return acc;
      },
      {}
    );

    _.forEach(_.sortBy(_.values(schoolMap), "label"), school => {
      const schoolOptions = buildOptionItems({
        columns: getSchoolGradeScales(school.sourceId),
        parentKey: school.key,
        sourceId: school.sourceId,
      });

      if (!_.isEmpty(schoolOptions)) {
        groupedOptions.push({
          key: school.key,
          label: school.label,
          isItemGroup: true,
          options: schoolOptions,
        });
      }
    });

    const customOptions = buildOptionItems({
      columns: getCustomColumns(),
      parentKey: CUSTOM_GROUP_KEY,
      sourceId: null,
    });

    if (!_.isEmpty(customOptions)) {
      groupedOptions.push({
        key: CUSTOM_GROUP_KEY,
        label: customLabel,
        isItemGroup: true,
        options: customOptions,
      });
    }

    return groupedOptions;
  }, [
    academicRecordsData,
    selectedGradeId,
    buildOptionItems,
    getSchoolGradeScales,
    getCustomColumns,
    transcriptGradesById,
    t,
  ]);

  const optionMeta = useMemo(() => {
    const keyToOptionMap = {};
    const availableKeySet = new Set();

    _.forEach(dropdownOptions, group => {
      _.forEach(_.get(group, "options", []), option => {
        const key = _.get(option, "key");
        if (!key) return;
        keyToOptionMap[key] = option;
        availableKeySet.add(key);
      });
    });

    return { keyToOptionMap, availableKeySet };
  }, [dropdownOptions]);

  const resolvedSelectedKeys = useMemo(() => {
    const { availableKeySet } = optionMeta;

    const normalizeKey = key => {
      const normalizedKey = _.toString(key || "");
      if (!normalizedKey) return "";

      const hasPrefix = idPrefix && _.startsWith(normalizedKey, idPrefix);
      const rawId = hasPrefix
        ? _.replace(normalizedKey, idPrefix, "")
        : normalizedKey;
      const mappedRawId = _.get(transcriptToSourceIdMap, rawId, rawId);

      return hasPrefix ? `${idPrefix}${mappedRawId}` : mappedRawId;
    };

    return _.uniq(
      _.compact(
        _.map(selectedKeys, key => {
          if (availableKeySet.has(key)) return key;

          const normalizedKey = normalizeKey(key);
          return availableKeySet.has(normalizedKey) ? normalizedKey : null;
        })
      )
    );
  }, [idPrefix, optionMeta, selectedKeys, transcriptToSourceIdMap]);

  const { keyToOptionMap } = optionMeta;

  const handleSelectionChange = useCallback(
    newSelectedKeys => {
      onSelectionChange({
        selectedKeys: newSelectedKeys,
        keyToOptionMap,
      });
    },
    [onSelectionChange, keyToOptionMap]
  );

  return {
    resolvedSelectedKeys,
    dropdownOptions,
    handleSelectionChange,
    keyToOptionMap,
    t,
  };
};
